<script setup lang="ts">
import { nextTick, onUnmounted, ref } from 'vue'

import { injectLayoutDrag, isAfterMidpoint } from '../../composables/useLayoutDrag'
import type { DashboardGridAncestor } from '../../models/builder.types'
import type { DashboardTab } from '../../models/layout.types'
import { samePath } from '../../service/layout/moveLayout'

import TabActionMenu from './TabActionMenu.vue'

import { useI18n } from '@/shared/i18n'
import { Icon, useToast } from '@/shared/ui'

defineOptions({ name: 'WidgetTabs' })

const props = withDefaults(
  defineProps<{
    tabs: readonly DashboardTab[]
    activeId: string
    editMode: boolean
    /** Where this strip lives; without it the tabs are not draggable (a preview, a spec). */
    location?: { ancestors: readonly DashboardGridAncestor[]; rowId: string; cellId: string } | null
    /**
     * `group` is v3's root tab group (`CustomDashboardSection.vue:46-128`): folder tabs on the
     * canvas with the section's own grip and delete beside them. `inline` is a plain underline
     * strip for the rare nested cell that carries more than one tab.
     */
    variant?: 'group' | 'inline'
  }>(),
  { location: null, variant: 'inline' },
)
const emit = defineEmits<{
  select: [tabId: string]
  add: []
  rename: [payload: { tabId: string; name: string }]
  duplicate: [tabId: string]
  remove: [tabId: string]
}>()

const { t } = useI18n()
const toast = useToast()
const drag = injectLayoutDrag()
const renamingId = ref<string | null>(null)
const draftName = ref('')
const inputs = ref<Record<string, HTMLInputElement | null>>({})

/** How long a section has to hover a tab before that tab opens to receive it. */
const HOVER_OPEN_MS = 450
let hoverOpen: ReturnType<typeof setTimeout> | null = null
let hoverOpenId: string | null = null

function setInput(tabId: string, element: unknown): void {
  inputs.value[tabId] = (element as HTMLInputElement | null) ?? null
}

async function startRename(tab: DashboardTab): Promise<void> {
  renamingId.value = tab.id
  draftName.value = tab.name
  emit('select', tab.id)
  await nextTick()
  inputs.value[tab.id]?.focus()
  inputs.value[tab.id]?.select()
}

/**
 * v3 rejected an empty name and put the caret back (`handleRename:410-414`) — the one piece of
 * that flow worth keeping. It did it through a 10 ms `setTimeout`; `nextTick` is the same
 * intent without the magic number, and the editor stays open so the user is never dropped back
 * to a tab still carrying its old name with no idea the rename failed.
 */
async function commitRename(tab: DashboardTab): Promise<void> {
  const name = draftName.value.trim()
  if (name.length === 0) {
    toast.error(t('dashboards.grid.tabNameRequired'))
    await nextTick()
    inputs.value[tab.id]?.focus()
    return
  }
  renamingId.value = null
  if (name !== tab.name) emit('rename', { tabId: tab.id, name })
}

function cancelRename(): void {
  renamingId.value = null
}

function canDrag(tab: DashboardTab): boolean {
  return Boolean(drag && props.location && props.editMode && renamingId.value !== tab.id)
}

function isDragged(tab: DashboardTab): boolean {
  const item = drag?.item.value
  return item?.kind === 'tab' && isHere(item) && item.tabId === tab.id
}

function isHere(at: {
  ancestors: readonly DashboardGridAncestor[]
  rowId: string
  cellId: string
}) {
  const here = props.location
  return Boolean(
    here &&
    at.rowId === here.rowId &&
    at.cellId === here.cellId &&
    samePath(at.ancestors, here.ancestors),
  )
}

/** The insertion line sits before tab `index`; `tabs.length` draws it after the last tab. */
function showsLineAt(index: number): boolean {
  const target = drag?.target.value
  return target?.kind === 'tab' && isHere(target) && target.index === index
}

function tabAncestors(tab: DashboardTab): DashboardGridAncestor[] {
  const here = props.location!
  return [...here.ancestors, { rowId: here.rowId, cellId: here.cellId, tabId: tab.id }]
}

/** A section hovering a tab drops at the end of that tab's grid. */
function receivesSection(tab: DashboardTab): boolean {
  const target = drag?.target.value
  return Boolean(
    props.location && target?.kind === 'row' && samePath(target.ancestors, tabAncestors(tab)),
  )
}

function cancelHoverOpen(): void {
  if (hoverOpen) clearTimeout(hoverOpen)
  hoverOpen = null
  hoverOpenId = null
}

onUnmounted(cancelHoverOpen)

function onTabDragStart(tab: DashboardTab, event: DragEvent): void {
  if (!drag || !props.location || !canDrag(tab)) return
  drag.start({ kind: 'tab', ...props.location, tabId: tab.id }, event)
}

function onTabOver(tab: DashboardTab, index: number, event: DragEvent): void {
  const here = props.location
  const item = drag?.item.value
  if (!drag || !here || !item) return
  if (item.kind === 'tab') {
    const after = isAfterMidpoint(event, event.currentTarget as Element, 'x')
    drag.over({ kind: 'tab', ...here, index: after ? index + 1 : index }, event)
    return
  }
  const claimed = drag.over(
    { kind: 'row', ancestors: tabAncestors(tab), index: tab.rows.length },
    event,
  )
  if (!claimed || tab.id === props.activeId || hoverOpenId === tab.id) return
  cancelHoverOpen()
  hoverOpenId = tab.id
  hoverOpen = setTimeout(() => {
    emit('select', tab.id)
    cancelHoverOpen()
  }, HOVER_OPEN_MS)
}

/** Empty strip space past the last tab appends; a section over it falls through to its row. */
function onStripOver(event: DragEvent): void {
  const here = props.location
  if (!drag || !here || drag.item.value?.kind !== 'tab') return
  drag.over({ kind: 'tab', ...here, index: props.tabs.length }, event)
}

function onDrop(event: DragEvent): void {
  cancelHoverOpen()
  drag?.drop(event)
}
</script>

<template>
  <nav
    class="widget-tabs flex items-end overflow-x-auto"
    :class="variant === 'group' ? 'gap-xs' : 'min-h-10 gap-3xs border-b border-border'"
    role="tablist"
    data-testid="widget-tabs"
    @dragover="onStripOver"
    @drop="onDrop"
  >
    <slot name="leading" />
    <div
      v-for="(tab, index) in props.tabs"
      :key="tab.id"
      class="widget-tabs__tab group/tab relative inline-flex shrink-0 items-center transition-colors"
      :class="[
        variant === 'group'
          ? [
              'h-11 gap-xs rounded-t-md px-md text-base shadow-sm',
              tab.id === activeId
                ? 'widget-tabs__tab--active bg-surface text-primary after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-accent'
                : 'bg-border-subtle text-muted hover:bg-surface hover:text-primary',
            ]
          : [
              'gap-3xs rounded-t-sm border-b-2 px-sm text-sm',
              tab.id === activeId
                ? 'widget-tabs__tab--active border-b-primary text-primary'
                : 'border-b-transparent text-muted hover:bg-surface-sunken hover:text-fg',
            ],
        { 'opacity-40': isDragged(tab), 'bg-primary-subtle text-primary': receivesSection(tab) },
        canDrag(tab) ? 'cursor-grab active:cursor-grabbing' : '',
      ]"
      :draggable="canDrag(tab)"
      :data-testid="`widget-tab-item-${tab.id}`"
      @dragstart="onTabDragStart(tab, $event)"
      @dragend="drag?.end()"
      @dragover="onTabOver(tab, index, $event)"
      @dragleave="cancelHoverOpen"
    >
      <span
        v-if="showsLineAt(index)"
        class="widget-tabs__drop-line pointer-events-none absolute inset-y-2xs left-0 z-raised w-0.5 rounded-full bg-accent"
        data-testid="widget-tab-drop-line"
      />
      <input
        v-if="renamingId === tab.id"
        :ref="(element) => setInput(tab.id, element)"
        v-model="draftName"
        class="widget-tabs__rename w-32 rounded-sm border border-primary bg-surface px-2xs py-3xs font-sans text-inherit text-fg outline-none"
        type="text"
        :aria-label="t('dashboards.grid.tabRename')"
        :data-testid="`widget-tab-rename-${tab.id}`"
        @keydown.enter.prevent="commitRename(tab)"
        @keydown.esc.prevent="cancelRename"
        @blur="commitRename(tab)"
      />
      <button
        v-else
        type="button"
        role="tab"
        class="widget-tabs__label cursor-pointer border-0 bg-transparent py-xs font-sans text-inherit focus-visible:outline-none"
        :class="{ 'font-semibold': tab.id === activeId }"
        :aria-selected="tab.id === activeId"
        :data-testid="`widget-tab-${tab.id}`"
        @click="emit('select', tab.id)"
        @dblclick="editMode && startRename(tab)"
      >
        {{ tab.name || t('dashboards.grid.untitledTab') }}
      </button>
      <Icon
        v-if="canDrag(tab)"
        name="grip-vertical"
        :size="12"
        class="widget-tabs__grip text-accent"
        aria-hidden="true"
      />
      <TabActionMenu
        v-if="editMode && renamingId !== tab.id"
        :tab-name="tab.name || t('dashboards.grid.untitledTab')"
        :can-remove="props.tabs.length > 1"
        @rename="startRename(tab)"
        @duplicate="emit('duplicate', tab.id)"
        @remove="emit('remove', tab.id)"
      />
    </div>

    <span
      v-if="showsLineAt(props.tabs.length)"
      class="widget-tabs__drop-line widget-tabs__drop-line--end pointer-events-none my-2xs w-0.5 self-stretch rounded-full bg-accent"
      data-testid="widget-tab-drop-line"
    />

    <button
      v-if="editMode"
      type="button"
      class="widget-tabs__add inline-flex shrink-0 cursor-pointer items-center justify-center border-0 transition-colors"
      :class="
        variant === 'group'
          ? 'h-11 w-12 rounded-t-lg bg-surface text-accent shadow-sm hover:bg-surface-sunken'
          : 'mb-2xs size-7 rounded-sm bg-transparent text-muted hover:bg-surface-sunken hover:text-primary'
      "
      :aria-label="t('dashboards.grid.tabAdd')"
      :title="t('dashboards.grid.tabAdd')"
      data-testid="widget-tab-add"
      @click="emit('add')"
    >
      <Icon name="plus" :size="variant === 'group' ? 16 : 13" />
    </button>
    <slot name="trailing" />
  </nav>
</template>
